import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Grid, Link, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowUpRight } from "@phosphor-icons/react";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import { Link as RouterLink } from "react-router-dom";
import LabeledTextArea from "../../Widgets/LabeledTextArea";
import LabeledDatePicker from "../../Widgets/LabeledDatePicker";

interface DiagnosticFormValues {
  additionalDiagnosis: string;
  dateOfDiagnosis: Date | null;
  diagnosingProviderName: string;
  diagnosingProviderContact: string;
}

export interface DiagnosticModalSaveValues {
  additionalDiagnosis: string;
  dateOfDiagnosis: Date | null;
  diagnosingProviderName: string;
  diagnosingProviderContact: string;
}

const INITIAL_FORM: DiagnosticFormValues = {
  additionalDiagnosis: "",
  dateOfDiagnosis: null,
  diagnosingProviderName: "",
  diagnosingProviderContact: "",
};

interface DiagnosticModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: DiagnosticModalSaveValues) => void;
  isSaving?: boolean;
  initialValues?: Partial<DiagnosticFormValues>;
  clientId: string | number;
}

export const DiagnosticModal: React.FC<DiagnosticModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
  clientId,
}) => {
  const [formValues, setFormValues] = useState<DiagnosticFormValues>({
    ...INITIAL_FORM,
    ...initialValues,
  });

  useEffect(() => {
    if (open) {
      setFormValues({ ...INITIAL_FORM, ...initialValues });
    }
  }, [open]);

  const handleChange =
    (field: keyof DiagnosticFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleDateChange = (field: keyof DiagnosticFormValues) => (value: Date | null) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    onSave({
      additionalDiagnosis: formValues.additionalDiagnosis,
      dateOfDiagnosis: formValues.dateOfDiagnosis,
      diagnosingProviderName: formValues.diagnosingProviderName,
      diagnosingProviderContact: formValues.diagnosingProviderContact,
    });
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Edit Additional Diagnosis" : "Add Additional Diagnosis"}
      maxWidth="xs"
      drawerContentSx={{ maxHeight: "calc(80vh - 160px)" }}
    >
      <Box sx={{ p: 0 }}>
        <Grid container spacing={7} sx={{ p: "24px 0px" }}>
          <LabeledTextArea
            label="Additional Diagnosis"
            value={formValues.additionalDiagnosis}
            onChange={handleChange("additionalDiagnosis")}
            multiline={false}
          />
          <LabeledDatePicker
            label="Date of Diagnosis"
            value={formValues.dateOfDiagnosis}
            onChange={handleDateChange("dateOfDiagnosis")}
            slotProps={{ popper: { disablePortal: false } }}
          />
        </Grid>

        <Alert severity="info" sx={{ mt: 7 }}>
          <Typography variant="body2" fontWeight={600}>
            Release of Information Consent
          </Typography>
          <Typography variant="body2">
            If you&apos;re actively collaborating with this provider, add them to the Care Team
            first to send an ROI to the caregiver.
          </Typography>
        </Alert>
        <Link
          component={RouterLink}
          to={`/clients/${clientId}/care-team`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 1 }}
        >
          <Typography variant="body2">Go to Care Team</Typography>
          <ArrowUpRight size={16} />
        </Link>

        <Grid container spacing={7} sx={{ mt: 0 }}>
          <LabeledTextArea
            label="Diagnosing Provider Name"
            value={formValues.diagnosingProviderName}
            onChange={handleChange("diagnosingProviderName")}
            multiline={false}
          />
          <LabeledTextArea
            label="Diagnosing Provider Contact Information"
            value={formValues.diagnosingProviderContact}
            onChange={handleChange("diagnosingProviderContact")}
            multiline={false}
          />
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: "16px" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" loading={isSaving} onClick={handleSave}>
            {isEditMode ? "Save Changes" : "Add Additional Diagnosis"}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default DiagnosticModal;
